import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { REASON_OPTIONS, fmtQ } from './srUtils';

const tid = (key) => key.replace(/[^A-Za-z0-9-]/g, '_');

const ItemInfo = ({ it }) => (
  <div className="min-w-0">
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="font-mono font-semibold text-sm text-[#1A1F2E]">{it.title}</span>
      {it.splitFrom && <Badge variant="outline" className="text-[10px]">split from {it.splitFrom}</Badge>}
      {it.returned > 0 && <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700">{fmtQ(it.returned)} on returns</Badge>}
    </div>
    <p className="text-xs text-gray-600 truncate">{[it.kind === 'bundle' && it.sortNo, it.design, it.color].filter(Boolean).join(' · ') || '—'}</p>
    <p className="text-xs text-gray-500">{it.stage}{it.location ? ` · ${it.location}` : ''}{it.qtyNow != null && it.kind === 'bundle' ? ` · ${fmtQ(it.qtyNow)} pcs` : ''}</p>
    <div className="flex flex-wrap gap-1 mt-1">
      {it.canReturn && it.poList.filter((p) => p.available_qty > 0).map((p) => (
        <Badge key={p.po_item_id} variant="outline" className="text-[10px] border-red-300 text-red-700 font-normal">
          {p.po_number}: {fmtQ(p.available_qty)} {p.unit} above the PO still returnable
        </Badge>
      ))}
      {!it.poList.length && it.kind === 'bundle' && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-800 font-normal">Not on a PO</Badge>}
      {it.noPo > 0 && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-800 font-normal">{fmtQ(it.noPo)} pcs not on a PO</Badge>}
    </div>
  </div>
);

const QtyInput = ({ it, pick, onChange }) => {
  const id = tid(it.key);
  if (it.fixed != null) {
    return (
      <label className="flex items-center gap-2 text-sm font-medium cursor-pointer" data-testid={`sr-whole-${id}`}>
        <Checkbox checked={Number(pick.qty) > 0} onCheckedChange={(v) => onChange({ qty: v ? String(it.fixed) : '' })} data-testid={`sr-whole-check-${id}`} />
        Return the whole bundle ({fmtQ(it.fixed)} pcs)
      </label>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <Input type="number" inputMode="decimal" min="0" step="any" value={pick.qty || ''} onChange={(e) => onChange({ qty: e.target.value })}
        placeholder="Pcs back" className="h-11 w-28 text-base font-semibold placeholder:text-sm placeholder:font-normal" data-testid={`sr-qty-${id}`} />
      {it.max > 0 ? (
        <>
          <span className="text-xs text-gray-500">max {fmtQ(it.max)}</span>
          <Button type="button" variant="outline" size="sm" onClick={() => onChange({ qty: String(it.max) })} data-testid={`sr-qty-all-${id}`}>All</Button>
        </>
      ) : <span className="text-xs text-gray-500">whole bundle — type its pcs</span>}
    </div>
  );
};

const ReasonInputs = ({ it, pick, onChange }) => (
  <div className="flex flex-col sm:flex-row gap-2">
    <Select value={pick.reason || 'EXCESS'} onValueChange={(v) => onChange({ reason: v })}>
      <SelectTrigger className="h-11 sm:w-56" data-testid={`sr-reason-${tid(it.key)}`}><SelectValue /></SelectTrigger>
      <SelectContent>{REASON_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`sr-reason-opt-${v}`}>{l}</SelectItem>)}</SelectContent>
    </Select>
    <Input value={pick.note || ''} onChange={(e) => onChange({ note: e.target.value })} className="h-11"
      placeholder={(pick.reason || 'EXCESS') === 'OTHER' ? 'Type the reason (needed for Other)' : 'Note (optional)'} data-testid={`sr-note-${tid(it.key)}`} />
  </div>
);

/** One returnable item of a delivery — Greige bundle, FOB Sort No or untagged FOB bundle. */
export const ItemRow = ({ it, pick = {}, onChange }) => {
  const active = Number(pick.qty) > 0;
  return (
    <div data-testid={`sr-item-${tid(it.key)}`}
      className={`rounded-lg border p-3 space-y-2 transition-colors duration-150 ${!it.canReturn ? 'border-gray-200 bg-gray-50' : active ? 'border-[#B45309] bg-amber-50/60' : 'border-[#E5DFD0] bg-white'}`}>
      <ItemInfo it={it} />
      {!it.canReturn
        ? <p className="text-xs text-gray-500 italic" data-testid={`sr-item-note-${tid(it.key)}`}>{it.note || 'Nothing to return'}</p>
        : (
          <>
            {it.note && <p className="text-xs text-amber-800">{it.note}</p>}
            <QtyInput it={it} pick={pick} onChange={onChange} />
            {active && <ReasonInputs it={it} pick={pick} onChange={onChange} />}
          </>
        )}
    </div>
  );
};
